import React, { useEffect, useState } from 'react';
import { Settings, Clock, Wrench, Mail, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';

const Maintenance: React.FC = () => {
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMaintenanceMessage();
  }, []);

  const loadMaintenanceMessage = async () => {
    try {
      if (!supabase) {
        setMessage('Notre site est actuellement en maintenance. Nous serons bientôt de retour !');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('site_settings')
        .select('message')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (error) throw error;

      if (data?.message) {
        setMessage(data.message);
      } else {
        setMessage('Notre site est actuellement en maintenance. Nous serons bientôt de retour !');
      }
    } catch (error) {
      console.error('Erreur lors du chargement du message:', error);
      setMessage('Notre site est actuellement en maintenance. Nous serons bientôt de retour !');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 2%, transparent 2.5%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 2%, transparent 2.5%)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Cercles décoratifs flottants */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-float-slow"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-accent-300/20 rounded-full blur-3xl animate-float-slower"></div>
      <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-float"></div>

      <div className="max-w-3xl w-full relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8 animate-float">
          <img
            src="/proxylogoblanc.png"
            alt="Proxy Emobility"
            className="h-16 sm:h-20 md:h-24 w-auto"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>

        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-8 sm:p-10 md:p-12 border border-white/20">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-primary-500/20 rounded-full blur-xl animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-primary-500 to-primary-600 p-6 rounded-full">
                <Settings className="w-12 h-12 sm:w-16 sm:h-16 text-white animate-spin-slow" style={{ animationDuration: '8s' }} />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-primary-900 mb-4">
            Maintenance en cours
          </h1>

          {/* Message */}
          {loading ? (
            <div className="flex justify-center items-center py-6">
              <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : (
            <p className="text-base sm:text-lg md:text-xl text-center text-gray-700 mb-8 leading-relaxed px-4">
              {message}
            </p>
          )}

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
            <div className="bg-gradient-to-br from-primary-50 to-white p-5 rounded-xl border border-primary-100 text-center transform hover:scale-105 transition-all">
              <div className="flex justify-center mb-3">
                <Wrench className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">Améliorations</h3>
              <p className="text-xs sm:text-sm text-gray-600">Nouvelles fonctionnalités</p>
            </div>

            <div className="bg-gradient-to-br from-accent-50 to-white p-5 rounded-xl border border-accent-100 text-center transform hover:scale-105 transition-all">
              <div className="flex justify-center mb-3">
                <Clock className="w-8 h-8 text-accent-600" />
              </div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">Bientôt de retour</h3>
              <p className="text-xs sm:text-sm text-gray-600">Quelques minutes</p>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-5 rounded-xl border border-primary-100 text-center transform hover:scale-105 transition-all">
              <div className="flex justify-center mb-3">
                <AlertCircle className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">Merci</h3>
              <p className="text-xs sm:text-sm text-gray-600">Pour votre patience</p>
            </div>
          </div>

          {/* Refresh Button */}
          <div className="flex justify-center mb-6">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
            >
              <RefreshCw className="w-5 h-5" />
              Actualiser la page
            </button>
          </div>

          {/* Contact Info */}
          <div className="border-t border-gray-200 pt-6">
            <p className="text-center text-sm sm:text-base text-gray-600 mb-4">
              Pour toute urgence, contactez-nous :
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <a
                href="mailto:hello@proxyemobility.com"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors text-sm sm:text-base"
              >
                <Mail className="w-5 h-5" />
                hello@proxyemobility.com
              </a>
              <a
                href="tel:+221771234567"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors text-sm sm:text-base"
              >
                <Clock className="w-5 h-5" />
                +221 77 123 45 67
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Text */}
        <p className="text-center text-white/80 mt-6 text-sm sm:text-base">
          © {new Date().getFullYear()} Proxy Emobility. Tous droits réservés.
        </p>
      </div>
    </div>
  );
};

export default Maintenance;
